import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import {
  Search,
  LayoutDashboard,
  Table as TableIcon,
  BarChart3,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { LoadingBar, PaginationControls, PillTabs } from "@/components/common";
import { useTran } from "@/hooks";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import { cn } from "@/lib/utils";
import { formatEmissionValue } from "../domains/formatting";
import {
  useDashboardConsolidatedMetricDetailStore,
  useDashboardConsolidatedMetricOverviewStore,
  useDashboardConsolidatedOrgDetailStore,
  useDashboardFiltersStore,
  useDashboardMetricDetailStore,
  useDashboardOrgDetailStore,
  useDashboardOverviewStore,
} from "../hooks/useDashboardStore";
import {
  pickOrgDetailStore,
  pickOverviewStore,
  syncOrgExpansionOnViewModeChange,
} from "../domains/overview-mode";
import { rowKeyOf } from "../stores/metric-detail.store";
import { consolidatedKeyOf } from "../stores/consolidated-metric-detail.store";
import OverviewViewToggle from "./OverviewViewToggle";
import OverviewViewModeToggle from "./OverviewViewModeToggle";
import OverviewMetricRow from "./OverviewMetricRow";
import OverviewOrgRow from "./OverviewOrgRow";
import ConsolidatedMetricRow from "./ConsolidatedMetricRow";
import ConsolidatedMetricDetailPanel from "./ConsolidatedMetricDetailPanel";
import MetricDetailPanel from "./MetricDetailPanel";
import OrgDetailPanel from "./OrgDetailPanel";
import OverviewSkeleton from "./skeleton/OverviewSkeleton";
import OverviewRowSkeleton from "./skeleton/OverviewRowSkeleton";
import DataTableSection from "./data-table-section/DataTableSection";
import { dashboardDataTableStore } from "../stores/data-table.store";
import {
  buildFrameworkLabelByCode,
  getFrameworkLabel,
} from "../utils/reporting-years";
import { useAuthStore } from "@/features/auth";

const TargetDataOverviewCard = observer(() => {
  const { t } = useTran();
  const overview = useDashboardOverviewStore();
  const { available, filters: dashboardFilters } = useDashboardFiltersStore();
  const consolidatedMetric = useDashboardConsolidatedMetricOverviewStore();
  const orgDetail = useDashboardOrgDetailStore();
  const consolidatedOrgDetail = useDashboardConsolidatedOrgDetailStore();
  const metricDetail = useDashboardMetricDetailStore();
  const consolidatedMetricDetail = useDashboardConsolidatedMetricDetailStore();
  const { user } = useAuthStore();

  const { groupBy, viewMode, search, setViewMode, setSearch } = overview;
  const { setSection, setTab, tab, isOverviewActive, overviewSection } =
    dashboardDataTableStore;
  const isMetricView = groupBy === "metric";
  const isMetricConsolidated = isMetricView && viewMode === "consolidated";
  const shouldAutoExpandDetails = user?.enableViewDetailsSwitch === true;

  const active = pickOverviewStore({
    groupBy,
    viewMode,
    flatOverview: overview,
    consolidatedMetric,
  });
  const activeOrgDetail = pickOrgDetailStore({
    viewMode,
    flatOrgDetail: orgDetail,
    consolidatedOrgDetail,
  });
  const {
    rows,
    total,
    page,
    pageSize,
    loading,
    hasLoadedOnce,
    setPage,
    setPageSize,
  } = active;

  useEffect(() => {
    if (
      !shouldAutoExpandDetails ||
      !isOverviewActive ||
      loading ||
      !Array.isArray(rows) ||
      rows.length === 0
    ) {
      return;
    }

    if (!isMetricView) {
      rows.forEach((row) => {
        if (!row?.orgId || activeOrgDetail.isExpanded(row.orgId)) return;
        activeOrgDetail.expand({ orgId: row.orgId });
      });
      return;
    }

    if (isMetricConsolidated) {
      rows.forEach((row) => {
        if (!row?.disclosureId) return;
        const year = row.year ?? null;
        const key = consolidatedKeyOf(row.disclosureId, year);
        if (consolidatedMetricDetail.isExpanded(key)) return;
        consolidatedMetricDetail.expand({
          disclosureId: row.disclosureId,
          year,
        });
      });
      return;
    }

    rows.forEach((row) => {
      if (!row?.disclosureId || row.year == null) return;
      const key = rowKeyOf(row.disclosureId, row.year);
      if (metricDetail.isExpanded(key)) return;
      metricDetail.expand({ disclosureId: row.disclosureId, year: row.year });
    });
  }, [
    activeOrgDetail,
    consolidatedMetricDetail,
    isMetricConsolidated,
    isMetricView,
    isOverviewActive,
    loading,
    metricDetail,
    rows,
    shouldAutoExpandDetails,
  ]);

  const reportingYearLabelByStartYear = useMemo(
    () =>
      new Map(
        (available.reportingYears || []).map((reportingYear) => [
          Number(reportingYear.startYear),
          formatReportingYearPeriod(reportingYear),
        ]),
      ),
    [available.reportingYears],
  );
  const formatYearLabel = (year) =>
    reportingYearLabelByStartYear.get(Number(year)) ?? year;
  const frameworkLabelByCode = useMemo(
    () => buildFrameworkLabelByCode(available.frameworkObjects),
    [available.frameworkObjects],
  );
  const formatFrameworkLabel = (framework) =>
    getFrameworkLabel(framework, frameworkLabelByCode);

  const handleViewModeChange = (next) => {
    if (!isMetricView) {
      syncOrgExpansionOnViewModeChange({
        next,
        current: viewMode,
        flatOrgDetail: orgDetail,
        consolidatedOrgDetail,
      });
    }
    setViewMode(next);
  };

  const handleGroupByChange = (next) => {
    if (next === "organization" && viewMode === "consolidated") {
      setViewMode("flat");
    }
    setSection(next);
  };

  const headerCopy = useMemo(() => {
    const copyByTab = {
      overview: {
        title: t("Target and Data Overview"),
        subtitle: t("Monitor target setting against data submissions."),
      },
      esg: {
        title: t("ESG Data Table"),
        subtitle: t(
          "Browse submitted ESG metrics by year, entity, and country.",
        ),
      },
      charts: {
        title: t("Emissions Overview"),
        subtitle: t(
          "Track total emissions and Scope 1, 2, and 3 breakdowns by year.",
        ),
      },
    };

    return copyByTab[tab] || copyByTab.overview;
  }, [t, tab]);

  if (!overview.hasLoadedOnce && overview.loading) return <OverviewSkeleton />;

  const searchPlaceholder =
    groupBy === "metric"
      ? t("Search metric by code or title...")
      : t("Search organization by name or code...");

  const isRefreshing = hasLoadedOnce && loading;

  return (
    <Card className="relative flex h-full w-full flex-col overflow-hidden rounded-xl border-border/70 bg-card/95 shadow-sm p-0">
      <CardHeader className="border-b px-3 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base font-bold text-foreground">
              {headerCopy.title}
            </CardTitle>
            <CardDescription className="text-xs font-normal text-muted-foreground">
              {headerCopy.subtitle}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {isOverviewActive && (
              <div className="relative w-48 sm:w-56">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="h-8 pl-7 text-xs"
                />
              </div>
            )}
            <PillTabs
              tabs={[
                {
                  value: "overview",
                  label: t("Overview"),
                  icon: LayoutDashboard,
                },
                { value: "esg", label: t("ESG data table"), icon: TableIcon },
                { value: "charts", label: t("E data charts"), icon: BarChart3 },
              ]}
              value={tab}
              onChange={setTab}
              size="sm"
            />
          </div>
        </div>
      </CardHeader>

      <DataTableSection />

      {isOverviewActive && isRefreshing && <LoadingBar />}

      {isOverviewActive && (
        <CardContent
          className={cn(
            "space-y-3 transition-opacity p-3",
            isRefreshing && "opacity-60",
          )}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            {dashboardFilters.frameworks.length === 1 &&
            Number.isFinite(overview.totalCo2eReduction) ? (
              <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-300">
                {t("Total CO₂e Reduction")}:{" "}
                {formatEmissionValue(overview.totalCo2eReduction)} kgCO₂e
              </span>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              <OverviewViewToggle
                value={overviewSection}
                onChange={handleGroupByChange}
              />
              {isMetricView && (
                <OverviewViewModeToggle
                  value={viewMode}
                  onChange={handleViewModeChange}
                />
              )}
            </div>
          </div>
          {!hasLoadedOnce || (loading && rows.length === 0) ? (
            <div className="space-y-2">
              {Array.from({ length: pageSize }).map((_, i) => (
                <OverviewRowSkeleton key={i} />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">
              {t("No results match your filters.")}
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((row) => {
                if (!isMetricView) {
                  const isExpanded = activeOrgDetail.isExpanded(row.orgId);
                  const panel = activeOrgDetail.getPanel(row.orgId);
                  return (
                    <div key={row.orgId}>
                      <OverviewOrgRow
                        row={row}
                        isExpanded={isExpanded}
                        onToggleDetail={() =>
                          activeOrgDetail.toggle({ orgId: row.orgId })
                        }
                      />
                      {isExpanded && panel && (
                        <OrgDetailPanel
                          store={panel}
                          formatFrameworkLabel={formatFrameworkLabel}
                        />
                      )}
                    </div>
                  );
                }
                if (isMetricConsolidated) {
                  const key = consolidatedKeyOf(
                    row.disclosureId,
                    row.year ?? null,
                  );
                  const isExpanded = consolidatedMetricDetail.isExpanded(key);
                  const panel = consolidatedMetricDetail.getPanel(key);
                  return (
                    <div key={key}>
                      <ConsolidatedMetricRow
                        row={row}
                        isExpanded={isExpanded}
                        formatYearLabel={formatYearLabel}
                        formatFrameworkLabel={formatFrameworkLabel}
                        onToggleDetail={() =>
                          consolidatedMetricDetail.toggle({
                            disclosureId: row.disclosureId,
                            year: row.year ?? null,
                          })
                        }
                      />
                      {isExpanded && panel && (
                        <ConsolidatedMetricDetailPanel panel={panel} />
                      )}
                    </div>
                  );
                }
                const key = rowKeyOf(row.disclosureId, row.year);
                const isExpanded = metricDetail.isExpanded(key);
                const panel = metricDetail.getPanel(key);
                return (
                  <div key={key}>
                    <OverviewMetricRow
                      row={row}
                      isExpanded={isExpanded}
                      formatYearLabel={formatYearLabel}
                      formatFrameworkLabel={formatFrameworkLabel}
                      onToggleDetail={() =>
                        metricDetail.toggle({
                          disclosureId: row.disclosureId,
                          year: row.year,
                        })
                      }
                    />
                    {isExpanded && panel && <MetricDetailPanel panel={panel} />}
                  </div>
                );
              })}
            </div>
          )}

          <PaginationControls
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[5, 10, 20, 50]}
            disabled={loading}
            listLabel={isMetricView ? t("Metrics") : t("Organizations")}
            hideWhenSinglePage
          />
        </CardContent>
      )}
    </Card>
  );
});

export default TargetDataOverviewCard;
